import { Injector } from '@angular/core';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { FormID } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

let _dayjsPluginsInitialized = false;

/**
 * Ensures required Day.js plugins are registered only once.
 * Safe to call multiple times.
 */
const ensureDayjsPluginsInitialized = (): void => {
  if (_dayjsPluginsInitialized) {
    return;
  }

  dayjs.extend(customParseFormat);
  dayjs.extend(isSameOrAfter);

  _dayjsPluginsInitialized = true;
};

/**
 * Common utility functions shared across payment-related workflows.
 * Contains helpers for accessing form data, validating dates,
 * and formatting scheduled payment values.
 */

/**
 * Retrieves flattened Initiate Payment form data.
 *
 * @param injector Angular dependency injector
 * @returns Initiate payment form data or `undefined`
 */
const getInitiatePaymentFormData = (injector: Injector) => {
  const workflowFormsService = injector.get(WorkflowFormsService);
  return workflowFormsService.getAllFormValuesFlattened()?.[FormID.INITIATE_PAYMENT_FORM];
};

/**
 * Returns a formatted scheduled payment date if it is valid.
 *
 * Business rules:
 * - Scheduled payment date must be today or a future date
 * - Past dates are not allowed
 *
 * @param injector Angular dependency injector
 * @returns Formatted date (`YYYY-MM-DD`) or `undefined` if missing or invalid
 */
export const formatSchedulePaymentDate = (injector: Injector): string | undefined => {
  const formData = getInitiatePaymentFormData(injector);
  const schedulePaymentDate = formData?.schedulePaymentDate;

  if (!schedulePaymentDate || !isValidScheduleDate(injector, schedulePaymentDate)) {
    return undefined;
  }

  return formatDate(schedulePaymentDate);
};

/**
 * Validates whether the scheduled payment date is today or a future date.
 *
 * If the date is invalid, an error alert is displayed to the user.
 *
 * @param injector Angular dependency injector
 * @param dateValue Optional date string to validate. If omitted,
 *                  the value is read from the Initiate Payment form.
 * @returns `true` if the date is valid; otherwise `false`
 */
export const isValidScheduleDate = (injector: Injector, dateValue?: string): boolean => {
  const schedulePaymentDate = dateValue ?? getInitiatePaymentFormData(injector)?.schedulePaymentDate;

  ensureDayjsPluginsInitialized();

  const isValid =
    !!schedulePaymentDate &&
    dayjs(schedulePaymentDate).isValid() &&
    dayjs(schedulePaymentDate).isSameOrAfter(dayjs(), 'day');

  if (schedulePaymentDate && !isValid) {
    const alertHandlerService = injector.get(AlertHandlerService);
    alertHandlerService.showAlertFn('error', 'Scheduled payment date must be today or a future date.');
    throw new Error('Scheduled payment date must be today or a future date.');
  }

  return true;
};

/**
 * Formats a date string using dayjs.
 *
 * @param dateValue Date string to format
 * @param format Desired output format (default: `YYYY-MM-DD`)
 * @returns Formatted date string
 */
const formatDate = (dateValue: string, format = 'YYYY-MM-DD'): string => {
  return dayjs(dateValue).format(format);
};
